import AdvanceSlicesFormBody from "components/AdvanceManagement/AdvanceSlicesFormBody";
import type { TAdvancePayment } from "components/AdvanceManagement/NewAdvanceModal";
import { formatInitialSlices, readTermAdvancePayments } from "components/AdvanceManagement/NewAdvanceModal/utils";
import type { TModalActionButton } from "components/shared/ModalActionButtons";
import ModalActionButtons from "components/shared/ModalActionButtons";
import { Spinner } from "components/shared/Spinner";
import { refetchManagementGeneralTab } from "helpers/refetchManagementGeneralTab";
import type { IHandleRequest } from "hooks/useAPIPagination";
import usePaymentStateUpdate from "hooks/usePaymentStateUpdate";
import { useEffect, useState } from "react";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { addAdvancePayment, updateAdvancePayment } from "store/advancePaymentSlice";
import type { GeneralTabTerm } from "store/managementStore";
import { useAppDispatch, useAppSelector } from "store/store";
import { fetchAdvancePaymentsOfTerm } from "store/termStore";
import { PaymentStatus } from "store/types";
import { v4 as uuidv4 } from "uuid";

import styles from "./AdvanceUpdate.module.scss";

interface IAdvanceUpdateOption {
    term: GeneralTabTerm | null;
    handleClose: VoidFunction;
    /**
     * Triggers animation on balance cell.
     */
    setBalanceUpdateSuccessful: React.Dispatch<
        React.SetStateAction<{
            termId: number | null;
            animate: boolean;
        }>
    >;
    /** From Management: same refetch as `useAPIPagination` `handleRequest` for the Général table. */
    refetchGeneralTab?: (params?: IHandleRequest) => void;
}

/**
 * Option on BalanceUpdateModal to update existing advances.
 */
const AdvanceUpdateOption = ({
    term,
    handleClose,
    setBalanceUpdateSuccessful,
    refetchGeneralTab,
}: IAdvanceUpdateOption) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();

    const { activePeriod, periodsDateRange } = useAppSelector((state) => state.period);
    const { updatePaymentPageState } = usePaymentStateUpdate(periodsDateRange);

    const [requestPending, setRequestPending] = useState(false);
    const [advancesPending, setAdvancesPending] = useState(true);

    const defaultValues: TAdvancePayment = {
        term: null,
        isForfait: 0,
        forceOver4Pass: 0,
        slices: [
            {
                amount: 0,
                paymentDate: null,
                status: PaymentStatus.NOT_PAID,
                comment: "",
                customStatusId: "",
                key: uuidv4(),
            },
        ],
    };

    const {
        control,
        watch,
        setValue,
        trigger,
        formState: { errors, dirtyFields },
        handleSubmit,
        reset,
    } = useForm<TAdvancePayment>({ defaultValues, mode: "all" });

    const { fields, append, remove } = useFieldArray({
        name: "slices",
        control,
    });

    const watchedSlices = useWatch({ control, name: "slices" });

    const removeRow = (index: number) => {
        remove(index);
    };

    const appendRow = () => {
        append({
            amount: 0,
            paymentDate: null,
            status: PaymentStatus.NOT_PAID,
            comment: "",
            customStatusId: "",
            key: uuidv4(),
            periodClosed: 0,
        });
    };

    const buttonsDetails: TModalActionButton = {
        cancel: { label: t("ModalBase.Cancel"), action: () => handleClose() },
        confirm: { label: t("ModalBase.Confirm"), type: "submit" },
    };

    const transformData = (data: TAdvancePayment["slices"], updatedIndexes: Array<number | null> | undefined) => {
        return data.map((slice, i) => {
            const paymentDate =
                slice.paymentDate === null ? null : new Date(slice.paymentDate!).toLocaleDateString("en-CA");
            return {
                ...slice,
                id: slice.sliceId,
                amount: Math.round(+slice.amount * 100),
                paymentDate,
                sliceRow: i,
                ...(updatedIndexes?.includes(i) ? { updated: true } : {}),
            };
        });
    };

    const onSubmit = async (formData) => {
        if (!term || !activePeriod) return;
        const slices = formData.slices;

        setRequestPending(true);

        // Find out which slices have been updated and set that property to true.
        const dirtyIndexes = dirtyFields.slices
            ?.map((item, index) => (item && Object.values(item).some((value) => value === true) ? index : null))
            .filter((index) => index !== null);

        const hasExistingAdvanceSlice = slices.some((s) => !!s.paymentId);
        const payloadSlices = transformData(slices, dirtyIndexes);
        const response = hasExistingAdvanceSlice
            ? await dispatch(
                  updateAdvancePayment({
                      termId: term.id,
                      data: {
                          periodId: activePeriod,
                          slices: payloadSlices,
                          isForfait: 0,
                          forceOver4Pass: 0,
                      },
                  }),
              )
            : await dispatch(
                  addAdvancePayment({
                      termId: term.id,
                      slices: payloadSlices,
                      isForfait: 0,
                      periodId: activePeriod,
                      forceOver4Pass: 0,
                  }),
              );

        if (response.meta.requestStatus === "fulfilled") {
            updatePaymentPageState();
            if (refetchGeneralTab) {
                refetchGeneralTab();
            } else {
                void refetchManagementGeneralTab(dispatch, activePeriod);
            }
            handleClose();
            setBalanceUpdateSuccessful({ termId: term.id, animate: true });
        }

        setRequestPending(false);
    };

    // Fetches advance slices linked to term.
    useEffect(() => {
        if (!term) return;
        if (term.hasAdvances) {
            dispatch(
                fetchAdvancePaymentsOfTerm({ termId: term.id, typeOfPayment: "advance", typeOfAdvance: "all" }),
            ).then((resp) => {
                if (resp.meta.requestStatus === "fulfilled") {
                    // Edit only one advance at a time in this modal: pick the latest one.
                    // The backend update path keys off slices[0].paymentId and rewrites that
                    // single advance's slices, so mixing slices from multiple advances would
                    // duplicate them. To manage multiple standalone advances on a term, the
                    // user can use the dedicated Advance Management page.
                    const fetched = readTermAdvancePayments(resp.payload);
                    const latestAdvance = fetched.length
                        ? fetched.reduce((acc, cur) => (cur.id > acc.id ? cur : acc), fetched[0])
                        : null;
                    const allSlices = latestAdvance ? formatInitialSlices([latestAdvance]) : [];

                    if (allSlices.length > 0) {
                        reset(
                            {
                                ...defaultValues,
                                slices: allSlices,
                            },
                            { keepDirty: false },
                        );
                    }
                }
                setAdvancesPending(false);
            });
        } else {
            setAdvancesPending(false);
        }
    }, [term]);

    return (
        <form className={styles.formContainer} onSubmit={handleSubmit(onSubmit)}>
            {advancesPending ? (
                <div className={styles.spinnerContainer}>
                    <Spinner />
                </div>
            ) : (
                <AdvanceSlicesFormBody
                    formData={{ control, watch, errors, setValue, trigger }}
                    fields={fields}
                    watchedSlices={watchedSlices ?? []}
                    appendRow={appendRow}
                    removeRow={removeRow}
                    isUpdate={Boolean(term?.hasAdvances)}
                    isForfait={0}
                    type="advance"
                    totalLabel={t("BalanceUpdate.NewAdvanceTotalAmount")}
                />
            )}

            <ModalActionButtons buttonsDetails={buttonsDetails} loading={requestPending} />
        </form>
    );
};

export default AdvanceUpdateOption;
